/**
 * Paginator utility that fetches items in pages and returns merged results
 *
 * @param total_items - Total number of items to paginate through
 * @param per_page - Number of items per page
 * @param callback - Function that receives (skip, limit) and returns array of items
 * @returns Array of all fetched items merged together
 *
 * @example
 * const all_users = await paginate({
 *   total_items: 1000,
 *   per_page: 100,
 *   callback: async (skip, limit) => {
 *     return await db.users.find().skip(skip).limit(limit).toArray();
 *   }
 * });
 */
export async function paginate<T>({
	total_items,
	per_page,
	callback,
}: {
	total_items: number;
	per_page: number;
	callback: (skip: number, limit: number) => Promise<T[]>;
}): Promise<T[]> {
	const results: T[] = [];
	let current_skip = 0;

	while (current_skip < total_items) {
		const limit = Math.min(per_page, total_items - current_skip);
		const page_results = await callback(current_skip, limit);

		results.push(...page_results);

		// If we got fewer items than requested, we've reached the end
		if (page_results.length < limit) {
			break;
		}

		current_skip += per_page;
	}

	return results;
}
